import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { LetsWorkTogether } from './ui/lets-work-section';

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      question: "What laser cutting services does Alpha Laser Technologies offer?",
      answer: "Alpha Laser Technologies Coimbatore offers comprehensive laser cutting services including precision metal laser cutting for Mild Steel (up to 22mm), Stainless Steel (up to 16mm), Copper (up to 3mm), Aluminum (up to 12mm), and acrylic laser cutting. We provide both sheet laser cutting and pipe/tube laser cutting services with ±0.1mm precision accuracy using advanced BODOR Fiber Laser technology."
    },
    {
      question: "What is the precision accuracy of Alpha Laser Technologies machines?",
      answer: "Alpha Laser Technologies uses BODOR Fiber Laser cutting machines that achieve ±0.1mm precision tolerance, making us one of the most accurate laser cutting service providers in Coimbatore and South India. This level of accuracy is suitable for aerospace, automotive, medical, and other precision-demanding industrial applications."
    },
    {
      question: "How fast is the turnaround time at Alpha Laser Technologies?",
      answer: "Alpha Laser Technologies offers 24-48 hour turnaround on standard laser cutting projects at our Coimbatore facility. Complex or high-volume orders may require additional time. We also provide express delivery services for urgent requirements across Coimbatore, Chennai, Bangalore, Hyderabad, and other cities in South India."
    },
    {
      question: "Where is Alpha Laser Technologies located?",
      answer: "Alpha Laser Technologies is located in Ganapathy, Coimbatore and serves the entire Coimbatore district and surrounding areas including SIDCO Industrial Estate, Kurichi, Peelamedu, Saravanampatti, Sulur, and Annur. We offer free pickup and delivery within Coimbatore for bulk orders."
    },
    {
      question: "Is Alpha Laser Technologies ISO certified?",
      answer: "Yes, Alpha Laser Technologies Coimbatore is ISO 9001:2015 certified. Our quality management system ensures consistent, high-quality output across all our laser cutting, CNC bending, welding, and surface coating services. This certification reflects our commitment to process excellence and customer satisfaction."
    },
    {
      question: "How do I get a quote from Alpha Laser Technologies?",
      answer: "Getting a quote from Alpha Laser Technologies is easy: Email your CAD files (DXF, DWG, STEP) to our team, call us directly, fill out our online quote form, or visit our Coimbatore facility at Ganapathy. We accept all standard CAD formats and typically respond with detailed quotes within 2-4 hours during business hours."
    }
  ];

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="py-20 bg-gradient-to-b from-gray-50 to-white" id="faq">
      <div className="max-w-4xl mx-auto px-6">
        {/* Section Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full mb-4">
            <span className="w-2 h-2 rounded-full bg-primary animate-pulse"></span>
            <span className="text-sm font-medium text-primary">Frequently Asked Questions</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-steel mb-4">
            <span className="text-primary">Alpha Laser Technologies</span>{' '}
            <span className="text-secondary">FAQ</span>
          </h2>
          <p className="text-lg text-steel-lighter max-w-2xl mx-auto">
            Find answers to common questions about Alpha Laser Technologies Coimbatore - our precision laser cutting, CNC bending, and metal fabrication services.
          </p>
        </motion.div>

        {/* FAQ Items */}
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: index * 0.05 }}
            >
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
                aria-expanded={openIndex === index}
              >
                <h3 className="text-lg font-semibold text-steel pr-8">
                  {faq.question}
                </h3>
                <motion.div
                  animate={{ rotate: openIndex === index ? 180 : 0 }}
                  transition={{ duration: 0.3 }}
                  className="flex-shrink-0"
                >
                  <svg
                    className={`w-6 h-6 transition-colors ${
                      openIndex === index ? 'text-primary' : 'text-gray-400'
                    }`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </motion.div>
              </button>
              
              <AnimatePresence>
                {openIndex === index && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3, ease: 'easeInOut' }}
                  >
                    <div className="px-6 pb-5 pt-0">
                      <div className="w-full h-px bg-gradient-to-r from-primary/20 via-secondary/20 to-transparent mb-4"></div>
                      <p className="text-steel-lighter leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>

        {/* CTA Section */}
        <motion.div
          className="mt-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <LetsWorkTogether />
        </motion.div>
      </div>

      {/* FAQ Schema Markup - For SEO */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": faqs.map(faq => ({
            "@type": "Question",
            "name": faq.question,
            "acceptedAnswer": {
              "@type": "Answer",
              "text": faq.answer
            }
          }))
        })
      }} />
    </section>
  );
};

export default FAQ;
